import React from 'react'
import { Form, Row, Col, DropdownButton, Dropdown, Button, Badge } from 'react-bootstrap'
import { DateTime } from 'luxon'

// definitions
import { AccountXeroOrgWithData } from '../../../../screens/subsync/subsync'
import { MessageAction } from '../../../ui/messages/message'
import { AppState } from '../../../../App.d'
import { DetailedStripeOrgAccountXeroOrg } from '../settings'
import { StripeOrgAccountXeroOrgSettings } from '../../../../../../back-end/common/stripeOrgAccountXeroOrgSettings'
import { StripeOrgAccountXeroOrgBankFeed } from '../../../../../../back-end/common/stripeOrgAccountXeroOrgBankFeed'

// utilities
import { FormDate } from '../../../ui/form/Date'
import * as Request from '../../../../utilities/request'

interface BankFeedSettingsProps {
	orgMapping: DetailedStripeOrgAccountXeroOrg | undefined
	mappedXeroOrg: AccountXeroOrgWithData | undefined
	stripeOrgID: string
	appState: AppState
	handleSettingsUpdate: (stripeOrgID: string, values: Partial<StripeOrgAccountXeroOrgSettings>) => void
	updateMessages: (action: MessageAction) => void
	refreshData: () => void
	// Owned by the settings screen (useBankFeed) because OrgMappingHeader needs the same connection status
	bankFeed: StripeOrgAccountXeroOrgBankFeed | undefined
	loadingBankFeed: boolean
	reloadBankFeed: () => Promise<void>
}

const BankFeedSettings = (props: BankFeedSettingsProps) => {
	const [startDate, setStartDate] = React.useState<string>(DateTime.now().plus({ days: 1 }).toISODate()!)
	const [isConnecting, setIsConnecting] = React.useState<boolean>(false)
	const [isDisconnecting, setIsDisconnecting] = React.useState<boolean>(false)

	const stripeOrgAccountXeroOrgID = props.orgMapping?.stripeOrgAccountXeroOrgID
	const { bankFeed, loadingBankFeed } = props

	const showMessage = (message: string, severity: 'danger' | 'success' = 'danger') =>
		props.updateMessages({ type: 'add', data: { severity, message, timeout: 4000, dismissible: true } })

	const bankAccounts = props.mappedXeroOrg?.accounts.filter((account) => account.type === 'BANK') || []
	const selectedBankAccount = bankAccounts.find(
		(account) => account.xeroAccountID === props.orgMapping?.stripeOrgAccountXeroOrgSettings?.bankFeedsXeroAccountID
	)

	const handleConnect = async () => {
		if (!stripeOrgAccountXeroOrgID || !props.orgMapping?.stripeOrgAccountXeroOrgSettings?.bankFeedsXeroAccountID) {
			showMessage('Choose a Xero bank account before connecting')
			return
		}
		setIsConnecting(true)
		try {
			const urlRes = await Request.get('bankfeed/url', props.appState.authState)
			sessionStorage.setItem(
				'subsyncBankFeedConnect',
				JSON.stringify({
					stripeOrgAccountXeroOrgID,
					xeroBankAccountID: props.orgMapping.stripeOrgAccountXeroOrgSettings.bankFeedsXeroAccountID,
					startDate,
				})
			)
			window.location.href = urlRes.data.url
		} catch (e) {
			showMessage('Unable to start the Xero Bank Feed connection')
			setIsConnecting(false)
		}
	}

	const handleDisconnect = async () => {
		if (!bankFeed?.stripeOrgAccountXeroOrgBankFeedID) {
			return
		}
		setIsDisconnecting(true)
		try {
			const res = await Request.del(`bankfeed/connection/${bankFeed.stripeOrgAccountXeroOrgBankFeedID}`, props.appState.authState)
			if (res.data.success === false) {
				showMessage(res.data.message || 'Unable to disconnect the Bank Feed')
			} else {
				showMessage('Bank Feed disconnected', 'success')
				await props.reloadBankFeed()
				props.refreshData()
			}
		} catch (e) {
			showMessage('Unable to disconnect the Bank Feed')
		} finally {
			setIsDisconnecting(false)
		}
	}

	const isActive = bankFeed?.status === 'ACTIVE'

	return (
		<>
			<Form.Group as={Row} className="d-flex align-items-center" style={{ height: '2.5rem' }}>
				<Col sm="2">
					<Form.Label htmlFor={`${props.stripeOrgID}-bank-feeds-enabled`} className="mb-0">
						Enable Stripe Bank Feed
					</Form.Label>
				</Col>
				<Col>
					<Form.Check
						type={'switch'}
						id={`${props.stripeOrgID}-bank-feeds-enabled`}
						name={'bankFeedsEnabled'}
						checked={!!props.orgMapping?.stripeOrgAccountXeroOrgSettings?.bankFeedsEnabled}
						onChange={(e) => props.handleSettingsUpdate(props.stripeOrgID, { [e.target.name]: e.target.checked })}
					/>
				</Col>
			</Form.Group>
			{props.orgMapping?.stripeOrgAccountXeroOrgSettings?.bankFeedsEnabled && (
				<>
					<Form.Group as={Row} style={{ height: '2.5rem' }}>
						<Form.Label column sm={2}>
							Xero Bank Account
						</Form.Label>
						<Col sm={4}>
							<DropdownButton
								id={`${props.stripeOrgID}-bank-feeds-account-select`}
								title={selectedBankAccount?.name ?? 'Select...'}
								onSelect={(eventKey) => props.handleSettingsUpdate(props.stripeOrgID, { bankFeedsXeroAccountID: eventKey ?? null })}
								disabled={isActive}
							>
								{bankAccounts.map((account) => (
									<Dropdown.Item key={account.xeroAccountID} eventKey={account.xeroAccountID}>
										{account.name} {account.number ? `(${account.number})` : ''}
									</Dropdown.Item>
								))}
							</DropdownButton>
						</Col>
					</Form.Group>
					{loadingBankFeed ? (
						<Form.Group as={Row}>
							<Col>Loading connection status...</Col>
						</Form.Group>
					) : isActive ? (
						<>
							<Form.Group as={Row} className="d-flex align-items-center">
								<Col sm={2}>Status</Col>
								<Col>
									<Badge variant="success">Connected</Badge>
									{bankFeed?.lastSyncAttemptTs && (
										<span className="ml-2">
											Last sync: {DateTime.fromISO(bankFeed.lastSyncAttemptTs).toFormat('dd/MM/yyyy HH:mm')} ({bankFeed.lastSyncResult})
										</span>
									)}
								</Col>
							</Form.Group>
							<Form.Group as={Row}>
								<Col sm={2} />
								<Col>
									<Button variant="secondary" disabled={isDisconnecting} onClick={handleDisconnect}>
										{isDisconnecting ? 'Disconnecting...' : 'Disconnect Bank Feed'}
									</Button>
								</Col>
							</Form.Group>
						</>
					) : (
						<>
							{bankFeed?.status === 'REJECTED' && (
								<Form.Group as={Row}>
									<Col sm={2} />
									<Col className="error">{bankFeed.lastError || 'Xero rejected this connection - try a different bank account.'}</Col>
								</Form.Group>
							)}
							<Form.Group as={Row} style={{ height: '2.5rem' }}>
								<Col sm={2}>
									<FormDate
										name="bankFeedsStartDate"
										label="Start Date"
										value={startDate}
										min={DateTime.now().toISODate()!}
										onChange={(e) => setStartDate((e.target as HTMLInputElement).value)}
									/>
								</Col>
							</Form.Group>
							<Form.Group as={Row}>
								<Col sm={2} />
								<Col>
									<Button
										variant="dark"
										disabled={isConnecting || !props.orgMapping?.stripeOrgAccountXeroOrgSettings?.bankFeedsXeroAccountID}
										onClick={handleConnect}
									>
										{isConnecting ? 'Connecting...' : 'Connect Bank Feed'}
									</Button>
								</Col>
							</Form.Group>
						</>
					)}
				</>
			)}
		</>
	)
}

export { BankFeedSettings }
